import React, { useState, useCallback } from 'react';
import {
  Activity,
  RefreshCw,
  Server,
  Globe,
  Zap,
  ShieldAlert,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Shield,
  Database,
  TrendingUp,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getOpsMetrics, OpsReportPeriod, Flag, AlarmState, OpsMetrics } from '../../services/opsHealthService';

const SEV_COLOUR: Record<Flag['sev'], string> = {
  CRITICAL: 'bg-red-50 border-red-300 text-red-700',
  HIGH: 'bg-orange-50 border-orange-300 text-orange-700',
  MEDIUM: 'bg-yellow-50 border-yellow-300 text-yellow-700',
  LOW: 'bg-blue-50 border-blue-300 text-blue-700',
};

const SEV_DOT: Record<Flag['sev'], string> = {
  CRITICAL: 'bg-red-500',
  HIGH: 'bg-orange-500',
  MEDIUM: 'bg-yellow-500',
  LOW: 'bg-blue-500',
};

function scoreColour(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-yellow-600';
  return 'text-red-600';
}

function scoreRingColour(score: number): string {
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#ca8a04';
  return '#dc2626';
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatPct(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function AlarmBadge({ alarm }: { alarm: AlarmState }) {
  if (alarm.state === 'ALARM') {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-xl">
        <XCircle size={14} className="text-red-500 flex-shrink-0" />
        <span className="text-xs font-medium text-red-700 truncate max-w-[220px]" title={alarm.name}>{alarm.name}</span>
      </div>
    );
  }
  if (alarm.state === 'INSUFFICIENT_DATA') {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl">
        <AlertTriangle size={14} className="text-slate-400 flex-shrink-0" />
        <span className="text-xs font-medium text-slate-500 truncate max-w-[220px]">{alarm.name}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-xl">
      <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
      <span className="text-xs font-medium text-green-700 truncate max-w-[220px]">{alarm.name}</span>
    </div>
  );
}

function StatCard({ label, value, sub, flag }: { label: string; value: string | number; sub?: string; flag?: boolean }) {
  return (
    <div className={`flex flex-col bg-white border rounded-lg px-4 py-2 min-w-[120px] shadow-sm relative overflow-hidden group ${flag ? 'border-red-200' : 'border-primary/30'}`}>
      <div className={`absolute inset-0 transition-colors ${flag ? 'bg-red-50/50' : 'bg-primary/5 group-hover:bg-primary/10'}`} />
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wider z-10">{label}</span>
      <span className={`text-2xl font-bold z-10 ${flag ? 'text-red-600' : 'text-navy'}`}>{value}</span>
      {sub && <span className="text-xs text-slate-400 z-10">{sub}</span>}
    </div>
  );
}

function HealthRing({ score }: { score: number }) {
  const r = 36;
  const circ = 2 * Math.PI * r;
  const filled = (score / 100) * circ;
  const colour = scoreRingColour(score);
  return (
    <div className="relative flex items-center justify-center w-24 h-24">
      <svg width="96" height="96" className="-rotate-90">
        <circle cx="48" cy="48" r={r} fill="none" stroke="#e2e8f0" strokeWidth="8" />
        <circle cx="48" cy="48" r={r} fill="none" stroke={colour} strokeWidth="8"
          strokeDasharray={`${filled} ${circ}`} strokeLinecap="round" />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className={`text-2xl font-bold ${scoreColour(score)}`}>{score}</span>
        <span className="text-[10px] text-slate-400 font-medium">/ 100</span>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title }: { icon: React.ElementType; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} className="text-primary" />
      <h3 className="text-sm font-bold text-navy uppercase tracking-wide">{title}</h3>
    </div>
  );
}

function DailyView({ m, period }: { m: OpsMetrics; period: OpsReportPeriod }) {
  const alarmsInAlarm = m.alarms.filter(a => a.state === 'ALARM').length;

  return (
    <div className="flex flex-col gap-6">
      {/* Health score + alarms */}
      <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
        <div className="p-5 flex flex-col sm:flex-row gap-6">
          <div className="flex flex-col items-center gap-2 shrink-0">
            <HealthRing score={m.healthScore} />
            <span className="text-xs text-slate-500">Platform Health</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert size={15} className="text-slate-400" />
              <span className="text-sm font-semibold text-navy">CloudWatch Alarms</span>
              {alarmsInAlarm > 0 && (
                <span className="text-xs font-bold px-2 py-0.5 bg-red-100 text-red-600 rounded-full">{alarmsInAlarm} IN ALARM</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {m.alarms.length === 0
                ? <span className="text-xs text-slate-400">No alarms found</span>
                : m.alarms.map(a => <AlarmBadge key={a.name} alarm={a} />)
              }
            </div>
          </div>
        </div>

        {/* Flags */}
        {m.flags.length > 0 && (
          <div className="border-t border-[#e2e8f0] p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={15} className="text-orange-500" />
              <span className="text-sm font-semibold text-navy">{m.flags.length} Flag{m.flags.length !== 1 ? 's' : ''} Detected</span>
            </div>
            <div className="flex flex-col gap-2">
              {m.flags.map((f, i) => (
                <div key={`${f.sev}-${f.area}-${i}`} className={`flex items-start gap-3 px-3 py-2 border rounded-xl ${SEV_COLOUR[f.sev]}`}>
                  <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${SEV_DOT[f.sev]}`} />
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-xs font-bold uppercase tracking-wide opacity-70">{f.sev}</span>
                    <span className="text-xs font-semibold">{f.area}:</span>
                    <span className="text-xs">{f.msg}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* ECS */}
        <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5">
          <SectionHeader icon={Server} title="ECS" />
          <div className="flex flex-wrap gap-2 mb-3">
            <StatCard label="Tasks" value={`${m.ecs.runningCount}/${m.ecs.desiredCount}`} flag={m.ecs.runningCount < m.ecs.desiredCount} />
            {m.ecs.pendingCount > 0 && <StatCard label="Pending" value={m.ecs.pendingCount} flag />}
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold text-slate-400 uppercase">CPU</span>
              <span className="text-sm font-medium text-navy">P50 {m.ecs.cpuP50}%</span>
              <span className={`text-sm font-medium ${m.ecs.cpuP95 > 80 ? 'text-red-600' : 'text-navy'}`}>P95 {m.ecs.cpuP95}%</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold text-slate-400 uppercase">Memory</span>
              <span className="text-sm font-medium text-navy">P50 {m.ecs.memP50}%</span>
              <span className={`text-sm font-medium ${m.ecs.memP95 > 85 ? 'text-red-600' : 'text-navy'}`}>P95 {m.ecs.memP95}%</span>
            </div>
          </div>
        </div>

        {/* API Gateway */}
        <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5">
          <SectionHeader icon={Globe} title="API Gateway" />
          <div className="flex flex-wrap gap-2 mb-3">
            <StatCard label="Requests" value={m.apiGateway.totalRequests.toLocaleString()} />
            <StatCard label="5xx Rate" value={formatPct(m.apiGateway.error5xxRate)} flag={m.apiGateway.error5xxRate > 0.02} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold text-slate-400 uppercase">Latency</span>
              <span className="text-sm font-medium text-navy">P50 {formatMs(m.apiGateway.latencyP50)}</span>
              <span className={`text-sm font-medium ${m.apiGateway.latencyP99 > 5000 ? 'text-red-600' : 'text-navy'}`}>P99 {formatMs(m.apiGateway.latencyP99)}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold text-slate-400 uppercase">Errors</span>
              <span className="text-sm font-medium text-navy">4xx {m.apiGateway.error4xxCount.toLocaleString()}</span>
              <span className="text-sm font-medium text-navy">5xx {m.apiGateway.error5xxCount.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* n8n */}
        <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap size={16} className="text-primary" />
              <h3 className="text-sm font-bold text-navy uppercase tracking-wide">n8n Workflows</h3>
            </div>
            {period === 'weekly' && (
              <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">24h data only</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            <StatCard label="Executions" value={m.n8n.totalExecutions.toLocaleString()} />
            <StatCard label="Failures" value={m.n8n.failedExecutions} flag={m.n8n.failureRate > 0.05} sub={formatPct(m.n8n.failureRate)} />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold text-slate-400 uppercase">Duration</span>
            <span className="text-sm font-medium text-navy">Avg {formatMs(m.n8n.avgDurationMs)}</span>
            <span className="text-sm font-medium text-navy">P95 {formatMs(m.n8n.p95DurationMs)}</span>
          </div>
          {m.n8n.slowestWorkflows.length > 0 && (
            <div className="mt-3">
              <span className="text-[11px] font-semibold text-slate-400 uppercase">Slowest</span>
              <div className="mt-1 flex flex-col gap-1">
                {m.n8n.slowestWorkflows.slice(0, 3).map(w => (
                  <div key={w.name} className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-600 truncate">{w.name}</span>
                    <span className="text-xs font-semibold text-slate-500 shrink-0">{formatMs(w.avgMs)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CloudFront + Security */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5">
          <SectionHeader icon={Globe} title="CloudFront" />
          <div className="flex flex-wrap gap-2">
            <StatCard label="Requests" value={m.cloudfront.totalRequests.toLocaleString()} />
            <StatCard label="Error Rate" value={`${m.cloudfront.errorRate}%`} flag={m.cloudfront.errorRate > 1} />
          </div>
        </div>
        <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5">
          <SectionHeader icon={ShieldAlert} title="Security" />
          <div className="flex flex-wrap gap-2">
            <StatCard label="IP Blocks" value={m.security.ipBlocks} flag={m.security.ipBlocks > 10} />
            <StatCard label="Rate Limit" value={m.security.rateLimitViolations} />
            <StatCard label="Policy Denied" value={m.security.policyViolations} flag={m.security.policyViolations > 50} />
          </div>
        </div>
      </div>
    </div>
  );
}

function WeeklyExtras({ m }: { m: OpsMetrics }) {
  return (
    <div className="flex flex-col gap-6 mt-6">
      {/* Costs */}
      {m.costs && (
        <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5">
          <SectionHeader icon={DollarSign} title="LLM Costs (7d)" />
          <div className="flex flex-wrap gap-2 mb-4">
            <StatCard label="Total" value={`$${m.costs.totalUsd.toFixed(2)}`} />
          </div>
          {m.costs.byProvider.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                    <th className="text-left px-3 py-2 text-[11px] font-bold text-slate-500 uppercase">Provider</th>
                    <th className="text-right px-3 py-2 text-[11px] font-bold text-slate-500 uppercase">Cost</th>
                    <th className="text-right px-3 py-2 text-[11px] font-bold text-slate-500 uppercase">Tokens</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {m.costs.byProvider.map(p => (
                    <tr key={p.provider} className="hover:bg-slate-50/50">
                      <td className="px-3 py-2 text-sm text-navy font-medium">{p.provider}</td>
                      <td className="px-3 py-2 text-sm text-right font-mono text-slate-700">${p.usd.toFixed(4)}</td>
                      <td className="px-3 py-2 text-sm text-right font-mono text-slate-500">{p.tokens.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* WAF */}
      {m.waf && (
        <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5">
          <SectionHeader icon={Shield} title="WAF (7d)" />
          <div className="flex flex-wrap gap-2">
            <StatCard label="Allowed" value={m.waf.allowedRequests.toLocaleString()} />
            <StatCard label="Blocked" value={m.waf.blockedRequests.toLocaleString()} flag={m.waf.blockRate > 0.05} />
            <StatCard label="Block Rate" value={formatPct(m.waf.blockRate)} />
          </div>
        </div>
      )}

      {/* DynamoDB throttles */}
      {m.dynamodb && m.dynamodb.some(d => d.readThrottles > 0 || d.writeThrottles > 0) && (
        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="p-5 pb-0">
            <SectionHeader icon={Database} title="DynamoDB Throttles (7d)" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                  <th className="text-left px-5 py-2 text-[11px] font-bold text-slate-500 uppercase">Table</th>
                  <th className="text-right px-5 py-2 text-[11px] font-bold text-slate-500 uppercase">Read Throttles</th>
                  <th className="text-right px-5 py-2 text-[11px] font-bold text-slate-500 uppercase">Write Throttles</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {m.dynamodb.map(d => (
                  <tr key={d.table} className="hover:bg-slate-50/50">
                    <td className="px-5 py-2 font-mono text-xs text-slate-700">{d.table}</td>
                    <td className={`px-5 py-2 text-right font-semibold text-sm ${d.readThrottles > 0 ? 'text-orange-600' : 'text-slate-400'}`}>{d.readThrottles}</td>
                    <td className={`px-5 py-2 text-right font-semibold text-sm ${d.writeThrottles > 0 ? 'text-orange-600' : 'text-slate-400'}`}>{d.writeThrottles}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* n8n workflow health */}
      {m.n8nWorkflowHealth && m.n8nWorkflowHealth.length > 0 && (
        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="p-5 pb-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={16} className="text-primary" />
                <h3 className="text-sm font-bold text-navy uppercase tracking-wide">n8n Workflow Health ({m.n8nWorkflowHealth.length} workflows)</h3>
              </div>
              <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full mb-3">execution data: 24h only</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                  <th className="text-left px-5 py-2 text-[11px] font-bold text-slate-500 uppercase">Workflow</th>
                  <th className="text-center px-4 py-2 text-[11px] font-bold text-slate-500 uppercase">Active</th>
                  <th className="text-right px-4 py-2 text-[11px] font-bold text-slate-500 uppercase">Success</th>
                  <th className="text-right px-4 py-2 text-[11px] font-bold text-slate-500 uppercase">Failed</th>
                  <th className="text-right px-5 py-2 text-[11px] font-bold text-slate-500 uppercase">Failure Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {m.n8nWorkflowHealth.slice(0, 30).map(w => (
                  <tr key={w.id} className={`hover:bg-slate-50/50 ${w.failureRate > 0.1 ? 'bg-red-50/30' : ''}`}>
                    <td className="px-5 py-2 text-xs text-slate-700 font-medium max-w-[240px] truncate" title={w.name}>{w.name}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${w.active ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'}`}>
                        {w.active ? 'ON' : 'OFF'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right text-sm text-slate-600">{w.successCount}</td>
                    <td className={`px-4 py-2 text-right text-sm font-semibold ${w.failureCount > 0 ? 'text-red-600' : 'text-slate-400'}`}>{w.failureCount}</td>
                    <td className={`px-5 py-2 text-right text-sm font-semibold ${w.failureRate > 0.1 ? 'text-red-600' : w.failureRate > 0 ? 'text-orange-500' : 'text-slate-400'}`}>
                      {w.failureRate > 0 ? formatPct(w.failureRate) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {m.n8nWorkflowHealth.length > 30 && (
            <div className="px-5 py-3 border-t border-[#e2e8f0] bg-slate-50/50">
              <span className="text-xs text-slate-400">Showing top 30 by failure rate. {m.n8nWorkflowHealth.length - 30} more workflows with 0 failures omitted.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const OpsHealth: React.FC = () => {
  const [period, setPeriod] = useState<OpsReportPeriod>('daily');
  const [refetchKey, setRefetchKey] = useState(0);

  const { data: metrics, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['ops-health', period, refetchKey],
    queryFn: () => getOpsMetrics(period),
    staleTime: 0,
    retry: 1,
  });

  const refresh = useCallback(() => setRefetchKey(k => k + 1), []);

  const lastRefreshed = dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : null;

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Activity size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Ops Health</h1>
              <p className="text-slate-500 text-sm font-medium">
                {lastRefreshed ? `Last refreshed ${lastRefreshed}` : 'Live platform metrics'}
                {metrics && <span className="ml-2 text-slate-400">·</span>}
                {metrics && <span className="ml-2 text-slate-400 text-xs">{new Date(metrics.generatedAt).toLocaleString()}</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
              {(['daily', 'weekly'] as OpsReportPeriod[]).map(p => (
                <button key={p} onClick={() => setPeriod(p)}
                  className={`flex-1 px-4 py-2 text-xs font-semibold capitalize transition-all ${period === p ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                  {p === 'daily' ? 'Daily (24h)' : 'Weekly (7d)'}
                </button>
              ))}
            </div>
            <button onClick={refresh} disabled={isLoading}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-50">
              <RefreshCw size={15} className={isLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </header>

        {isLoading && (
          <div className="flex flex-col gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white border border-[#e2e8f0] rounded-2xl p-5 animate-pulse">
                <div className="h-4 bg-slate-100 rounded w-32 mb-3" />
                <div className="flex gap-2">
                  {[1, 2, 3].map(j => (
                    <div key={j} className="h-16 w-28 bg-slate-100 rounded-lg" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {error && !isLoading && (
          <div className="bg-white border border-red-200 rounded-2xl p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
              <XCircle size={24} className="text-red-500" />
            </div>
            <p className="text-sm font-semibold text-red-700">Failed to load metrics</p>
            <p className="text-xs text-slate-400 mt-1">Check backend logs or CloudWatch access.</p>
            <button onClick={refresh}
              className="mt-4 flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors mx-auto">
              <RefreshCw size={14} /> Retry
            </button>
          </div>
        )}

        {metrics && !isLoading && (
          <>
            <DailyView m={metrics} period={period} />
            {period === 'weekly' && <WeeklyExtras m={metrics} />}
            {period === 'weekly' && !metrics.costs && !metrics.waf && (
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-center">
                <Clock size={20} className="text-slate-300 mx-auto mb-2" />
                <p className="text-xs text-slate-400">Weekly-only metrics (WAF, costs, workflow table) require additional env vars: <code className="font-mono">N8N_LITELLM_ADMIN_OVERVIEW_WEBHOOK_URL</code></p>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default OpsHealth;
